/**
 * Theme configuration
 * Integrates Material-UI theme with custom theme system
 */

import { createTheme, Theme } from '@mui/material/styles';
import { darkPalette, lightPalette } from './palette';
import { typography } from './typography';
import { shapes } from './shapes';
import { layout } from './layout';




export const createAppTheme = (mode: 'light' | 'dark'): Theme => {
  const palette = mode === 'dark' ? darkPalette : lightPalette;

  const theme = createTheme({
    palette,
    typography,
    spacing: 8, // Material-UI uses 8px as base unit
    shape: {
      borderRadius: shapes.borderRadius.md,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: shapes.borderRadius.md,
            textTransform: 'none',
            fontWeight: 500,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: shapes.borderRadius.md,
            boxShadow: mode === 'dark' ? shapes.shadow.dark.md : shapes.shadow.md,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: shapes.borderRadius.md,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            overflowX: 'hidden',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            overflowX: 'hidden',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: shapes.borderRadius.md,
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12) transparent' : 'rgba(0, 0, 0, 0.12) transparent',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
              },
            },
          },
          // Standard scrollbars for everything else
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12) transparent' : 'rgba(0, 0, 0, 0.12) transparent',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
              },
            },
          },
        },
      },
    },
    // Custom properties
    customColors: palette.customColors!,
    shapes,
    layout,
  } as any); // Use any to bypass strict PaletteOptions check as we augment it below

  return theme;
};

export type AppTheme = Theme;

